import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ICD_ORDER_TYPE_LIST } from '@core/constants/ICD';
import { AuthorizationDiagnosisPac } from '@core/model';
import { LookupPacImService } from '@core/service';
import { ICDCode, ICDOrderType } from '@shared/model';
import { MessageService, SelectItem } from 'primeng/api';
import { finalize, map } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { ListboxClickEvent } from 'primeng/listbox';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'diagnosis-tab-pac',
    templateUrl: './diagnosis-tab-pac.component.html',
    styleUrls: ['./diagnosis-tab-pac.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DiagnosisTabPacComponent implements OnInit, OnDestroy {
  @Input() isInternalFlow!: boolean;
  @Input() diagnosis: AuthorizationDiagnosisPac[] | null;
  @Input() tenantId: string;

  @Output() cancelProcess = new EventEmitter<void>();
  @Output() backStep = new EventEmitter<void>();
  @Output() updateDiagnoses = new EventEmitter<AuthorizationDiagnosisPac[]>();
  @Output() submitStep = new EventEmitter<AuthorizationDiagnosisPac[]>();

  searchForm: FormGroup;
  diagnosisForm: FormGroup;

  isLoading = false;
  isFormValid = false;

  ICDs: ICDCode[] = [];
  availableICDs: SelectItem<ICDCode>[] = [];
  selectedICDs: SelectItem<ICDCode>[] = [];

  private isUnknownICD = false;
  private subs = new SubSink();
  private readonly unknownICDCode = 'Unknown';
  private readonly ICDsCodeMaxCount = 4;

  readonly orderTypeList: ICDOrderType[] = [...ICD_ORDER_TYPE_LIST];

  get canAddICDs(): boolean {
    if (this.isUnknownICD) {
      return false;
    }

    return this.selectedICDs.length < this.ICDsCodeMaxCount;
  }

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly lookupPacImService: LookupPacImService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.buildForms();

    if (this.diagnosis && this.diagnosis.length > 0) {
      this.setDefaultValues();
    }

    this.setFormValidity();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onClickBack(): void {
    this.backStep.emit();
  }

  onCancel(): void {
    this.cancelProcess.emit();
  }

  onSubmit(): void {
    const result = this.getResult();
    this.submitStep.emit(result);
  }

  onUpdate(): void {
    const result = this.getResult();

    if (this.isFormValid) {
      this.updateDiagnoses.emit(result);
    } else {
      this.updateDiagnoses.emit([]);
    }
  }

  getResult(): AuthorizationDiagnosisPac[] {
    return this.selectedICDs.map((item: SelectItem<ICDCode>, index: number) => ({
      icdCodeId: item.value.icdCodeID,
      icdOrderType: this.orderTypeList[index],
      icdCode: item.value,
    }));
  }

  searchICD(): void {
    const searchText = this.searchForm.get('icdCode').value;
    this.availableICDs = [];
    this.ICDs = [];
    this.isLoading = true;
    this.subs.sink = this.lookupPacImService
      .getIcdCodes(this.tenantId, searchText)
      .pipe(
        map((res) => {
          if (res?.length) {
            return res
              .filter(
                ({ icdCodeID }) =>
                  !this.selectedICDs.find(
                    ({ value: selectedICD }) =>
                      icdCodeID === selectedICD.icdCodeID
                  )
              )
              .filter((icd) =>
                !this.isInternalFlow
                  ? icd.code.toLocaleLowerCase() !== this.unknownICDCode.toLowerCase()
                  : icd)
              .sort((firstCode, secondCode) => firstCode.code.localeCompare(secondCode.code));
          }

          return [];
        }),
        finalize(() => {
          this.isLoading = false;
          this.ref.markForCheck();
        })
      )
      .subscribe((res) => {
        this.ICDs = res;
        this.availableICDs = this.ICDs.map((icd) =>
          this.createICDSelectItem(icd));

        if (res.length === 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'No ICD Codes Found',
            detail: 'No ICD Codes were found matching that text.',
          });
        }
      });
  }

  selectICD(select: ListboxClickEvent): void {
    this.isUnknownICD = this.checkICDUnknown(select.option.value);
    this.selectedICDs = this.isUnknownICD
      ? [select.option]
      : [...this.selectedICDs, select.option];
    this.availableICDs.splice(
      this.availableICDs.findIndex((icd) => icd.value === select.option.value),
      1
    );
    this.isFormValid = this.checkFormValidity();

    this.onUpdate();
  }

  removeICD(event: SelectItem<ICDCode>): void {
    const isCodeAvailable = this.ICDs.find(
      (el) => el.icdCodeID === event.value.icdCodeID
    );

    if (isCodeAvailable) {
      this.availableICDs = [...this.availableICDs, event].sort((firstIcd, secondIcd) =>
        firstIcd.label.localeCompare(secondIcd.label));
    }

    if (this.checkICDUnknown(event.value)) {
      this.isUnknownICD = false;
    }

    this.selectedICDs.splice(
      this.selectedICDs.findIndex((icd) => icd.value === event.value),
      1
    );
    this.isFormValid = this.checkFormValidity();

    this.onUpdate();
  }

  moveSelectedICDUp(icd: ICDCode): void {
    const selectedIndex = this.selectedICDs.findIndex((code) => code.value === icd);
    const temp = this.selectedICDs[selectedIndex];
    this.selectedICDs[selectedIndex] = this.selectedICDs[selectedIndex - 1];
    this.selectedICDs[selectedIndex - 1] = temp;

    this.onUpdate();
  }

  moveSelectedICDDown(icd: ICDCode): void {
    const selectedIndex = this.selectedICDs.findIndex((code) => code.value === icd);
    const temp = this.selectedICDs[selectedIndex];
    this.selectedICDs[selectedIndex] = this.selectedICDs[selectedIndex + 1];
    this.selectedICDs[selectedIndex + 1] = temp;

    this.onUpdate();
  }

  private createICDSelectItem(icd: ICDCode): SelectItem<ICDCode> {
    return {
      value: icd,
      label: `${icd.code} - ${icd.description}`,
    };
  }

  private buildForms(): void {
    this.searchForm = new FormGroup({
      icdCode: new FormControl(null, [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(10),
      ]),
    });
    this.diagnosisForm = new FormGroup({
      selectedIcd: new FormControl([]),
    });
  }

  private checkICDUnknown(icd: ICDCode): boolean {
    return icd.code === this.unknownICDCode;
  }

  private checkFormValidity(): boolean {
    return this.selectedICDs.length > 0;
  }

  private setDefaultValues(): void {
    this.selectedICDs = this.diagnosis.map((el) => {
      this.isUnknownICD = this.checkICDUnknown(el.icdCode);

      return this.createICDSelectItem(el.icdCode);
    });
  }

  private setFormValidity(): void {
    this.isFormValid = this.selectedICDs && this.selectedICDs.length > 0;
  }
}
